import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function AccountGroupForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        account_head_id: '',
        firm_bank_account_id: '',
        opening_balance: '0.00',
        is_active: true
    });

    const [accountHeads, setAccountHeads] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadAccountHeads();
        loadBankAccounts();
        if (isEdit) {
            loadAccountGroup();
        }
    }, [id]);

    const loadAccountHeads = async () => {
        try {
            const response = await accountingApi.getAccountHeads();
            if (response.success) {
                const heads = Array.isArray(response.data) ? response.data : (response.data?.account_heads || response.data?.data || []);
                setAccountHeads(heads);
            } else {
                console.error('Error loading account heads:', response.message);
            }
        } catch (error) {
            console.error('Error loading account heads:', error);
        }
    };

    const loadBankAccounts = async () => {
        try {
            const response = await accountingApi.getBankAccounts();
            if (response.success) {
                const accounts = Array.isArray(response.data) ? response.data : (response.data?.bank_accounts || response.data?.data || []);
                setBankAccounts(accounts);
            } else {
                console.error('Error loading bank accounts:', response.message);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    const loadAccountGroup = async () => {
        try {
            const response = await accountingApi.getAccountGroup(id);
            if (response.success) {
                const group = response.data?.account_group || response.data;
                if (group) {
                    setFormData({
                        name: group.name || '',
                        description: group.description || '',
                        account_head_id: group.account_head_id || '',
                        firm_bank_account_id: group.firm_bank_account_id || '',
                        opening_balance: group.opening_balance || '0.00',
                        is_active: group.is_active !== undefined ? group.is_active : true
                    });
                }
            } else {
                setErrors([response.message || 'Failed to load account group']);
            }
        } catch (error) {
            setErrors(['Failed to load account group']);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setErrors([]);

            const response = isEdit
                ? await accountingApi.updateAccountGroup(id, formData)
                : await accountingApi.createAccountGroup(formData);

            if (response.success) {
                const groupData = response.data?.account_group || response.data;
                if (groupData?.id) {
                    navigate(`/accounting/account-groups/${groupData.id}`);
                } else {
                    navigate('/accounting/account-groups');
                }
            } else {
                setErrors(response.errors || [response.message || 'Failed to save account group']);
            }
        } catch (error) {
            setErrors(['Failed to save account group']);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting/account-groups" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">
                        {isEdit ? 'Edit Account Group' : 'New Account Group'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {errors.length > 0 && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                        <ul className="list-disc list-inside text-sm text-rose-700">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Group Name <span className="text-rose-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                            placeholder="e.g., Cash, Bank, Debtors"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Account Head <span className="text-rose-500">*</span></label>
                        <select
                            name="account_head_id"
                            value={formData.account_head_id}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        >
                            <option value="">Select Account Head</option>
                            {accountHeads.map(head => (
                                <option key={head.id} value={head.id}>
                                    {head.name} ({head.account_type})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Bank Account (Optional)</label>
                        <select
                            name="firm_bank_account_id"
                            value={formData.firm_bank_account_id}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        >
                            <option value="">No linked bank account</option>
                            {bankAccounts.map(account => (
                                <option key={account.id} value={account.id}>
                                    {account.bank_name} - {account.account_number}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Opening Balance (₹)</label>
                        <input
                            type="number"
                            name="opening_balance"
                            value={formData.opening_balance}
                            onChange={handleChange}
                            step="0.01"
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                            placeholder="0.00"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all resize-none"
                            placeholder="Additional details about this account group..."
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleChange}
                                className="w-5 h-5 text-indigo-600 border-2 border-slate-300 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm font-semibold text-slate-700">Active</span>
                        </label>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update Group' : 'Create Group')}
                    </button>
                    <Link
                        to="/accounting/account-groups"
                        className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors text-center"
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default AccountGroupForm;
